import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import { btn } from "../styles/shared";
import { getTodayDate } from "../utils/formatting";
import { calcMacros } from "../utils/calculations";

const CARD = { background: "linear-gradient(135deg,#100a1e,#0a0714)", border: "1px solid rgba(167,139,250,0.15)", borderRadius: 16 };
const MONO = { fontFamily: "'Space Mono',monospace" };
const MAX_IMPORT_BYTES = 10 * 1024 * 1024;
const IMPORT_EXTENSIONS = new Set(["csv", "xlsx", "xls"]);
const QUICK_BREADS = ["bread", "brown bread", "sourdough bread", "wholewheat bread"];
const QUICK_SMOOTHIE_INGREDIENTS = [
  "pea protein",
  "blueberries",
  "strawberries",
  "apple",
  "pineapple",
  "banana",
  "mixed berries",
  "sugar",
  "soy milk",
  "greek yogurt",
  "maca powder",
];
const QUICK_MEAL_DEFAULTS = {
  Breakfast: [
    { name: "oats", amount: 0.5, unit: "cups" },
    { name: "pea protein", amount: 0.33, unit: "cups" },
  ],
  Lunch: [
    { name: "bread", amount: 80, unit: "g", kind: "bread" },
    { name: "eggs", amount: 4, unit: "egg", gramsPerUnit: 50 },
    { name: "cottage cheese", amount: 50, unit: "g", optional: true, enabled: true },
  ],
  "Protein Shake": [
    { name: "pea protein", amount: 0.33, unit: "cups", smoothie: true },
    { name: "blueberries", amount: 100, unit: "g", smoothie: true },
  ],
};

function validateImportFile(file) {
  const extension = String(file?.name || "").split(".").pop().toLowerCase();
  if (!IMPORT_EXTENSIONS.has(extension)) throw new Error("Choose a CSV, XLSX, or XLS spreadsheet.");
  if (!file.size) throw new Error("The selected spreadsheet is empty.");
  if (file.size > MAX_IMPORT_BYTES) throw new Error("Spreadsheet is too large. Maximum import size is 10 MB.");
}

// Try to detect which column index maps to each nutrient field
function detectCol(headers, candidates) {
  return headers.findIndex(h =>
    candidates.some(c => String(h).toLowerCase().trim().includes(c))
  );
}

// Scan the first 6 rows and return the index of the row that looks most like headers
// (handles spreadsheets with a merged title row above the actual column headers)
function findHeaderRow(rows) {
  const keywords = ["name","food","item","ingredient","product","description","cal","kcal","energy","calorie","protein","pro","prot","carb","cho","fat","lipid","fiber","fibre","date","meal","amount","qty","unit","note"];
  let bestIdx = 0, bestScore = -1;
  for (let i = 0; i < Math.min(rows.length, 6); i++) {
    const score = rows[i].filter(h => {
      const s = String(h).toLowerCase().trim();
      return s.length > 0 && keywords.some(k => s.includes(k));
    }).length;
    if (score > bestScore) { bestScore = score; bestIdx = i; }
  }
  return bestIdx;
}

function mapHeaders(headers) {
  return {
    name:     detectCol(headers, ["name","food","item","ingredient","product","description"]),
    calories: detectCol(headers, ["cal","kcal","energy","calorie"]),
    protein:  detectCol(headers, ["protein","pro","prot"]),
    carbs:    detectCol(headers, ["carb","cho","carbohydrate"]),
    fat:      detectCol(headers, ["fat","lipid"]),
    fiber:    detectCol(headers, ["fiber","fibre","dietary"]),
  };
}

function roundNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n * 1000) / 1000 : String(value || "").trim();
}

function ingredientFingerprint(ing) {
  const n = ing?._nutrients || {};
  return JSON.stringify({
    name: String(ing?.name || "").trim().toLowerCase(),
    amount: roundNumber(ing?.amount),
    unit: String(ing?.unit || "g").trim().toLowerCase(),
    calories: roundNumber(n.calories),
    protein: roundNumber(n.protein),
    carbs: roundNumber(n.carbs),
    fat: roundNumber(n.fat),
    fiber: roundNumber(n.fiber),
  });
}

function ingredientBatchExists(existing = [], incoming = []) {
  if (!existing.length || !incoming.length || incoming.length > existing.length) return false;
  const existingKeys = existing.map(ingredientFingerprint);
  const incomingKeys = incoming.map(ingredientFingerprint);

  for (let i = 0; i <= existingKeys.length - incomingKeys.length; i++) {
    if (incomingKeys.every((key, j) => existingKeys[i + j] === key)) return true;
  }

  return false;
}

// Peek at the first sheet's header row and return "meallog" or "foodlibrary"
function sniffFormat(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        const hi   = findHeaderRow(rows);
        const hdrs = rows[hi].map(h => String(h).toLowerCase().trim());
        const hasDate = hdrs.some(h => h === "date");
        const hasMeal = hdrs.some(h => h === "meal" || h === "meal type");
        const hasIngredient = hdrs.some(h => ["ingredient","food","item","name"].includes(h));
        // Route as meal log if it has a date column, even without a meal column
        resolve(hasDate && (hasMeal || hasIngredient) ? "meallog" : "foodlibrary");
      } catch { resolve("foodlibrary"); }
    };
    reader.onerror = () => resolve("foodlibrary");
    reader.readAsArrayBuffer(file);
  });
}

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb   = XLSX.read(data, { type: "array" });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (rows.length < 2) return reject(new Error("Spreadsheet is empty or has only headers"));
        const headerIdx = findHeaderRow(rows);
        const headers = rows[headerIdx].map(h => String(h));
        const colMap  = mapHeaders(headers);
        if (colMap.name < 0)     return reject(new Error("Could not find a 'name' column. Make sure your spreadsheet has a column called 'Name', 'Food', or 'Ingredient'."));
        if (colMap.calories < 0) return reject(new Error("Could not find a calories column. Expected a column called 'Calories', 'Cal', or 'kcal'."));
        const foods = [];
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          const name = String(row[colMap.name] || "").trim();
          if (!name) continue;
          const calories = parseFloat(row[colMap.calories]) || 0;
          if (calories === 0 && colMap.calories >= 0) continue; // skip rows with no calorie data
          foods.push({
            name,
            calories,
            protein: colMap.protein >= 0 ? (parseFloat(row[colMap.protein]) || 0) : 0,
            carbs:   colMap.carbs   >= 0 ? (parseFloat(row[colMap.carbs])   || 0) : 0,
            fat:     colMap.fat     >= 0 ? (parseFloat(row[colMap.fat])     || 0) : 0,
            fiber:   colMap.fiber   >= 0 ? (parseFloat(row[colMap.fiber])   || 0) : 0,
          });
        }
        if (foods.length === 0) return reject(new Error("No valid rows found. Make sure each row has a name and calorie value."));
        resolve({ foods, headers, colMap });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

// ── Meal log import helpers ────────────────────────────────────────────────

function mapMealLogHeaders(headers) {
  return {
    date:       detectCol(headers, ["date"]),
    meal:       detectCol(headers, ["meal","type"]),
    ingredient: detectCol(headers, ["ingredient","food","name","item","product"]),
    amount:     detectCol(headers, ["amount","qty","quantity","weight","grams"]),
    unit:       detectCol(headers, ["unit"]),
    calories:   detectCol(headers, ["kcal","cal","calorie","energy"]),
    protein:    detectCol(headers, ["protein","pro","prot"]),
    carbs:      detectCol(headers, ["carb","cho","carbohydrate"]),
    fat:        detectCol(headers, ["fat","lipid"]),
    fiber:      detectCol(headers, ["fiber","fibre","dietary"]),
    notes:      detectCol(headers, ["note","comment","remark"]),
  };
}

// Normalise meal names from spreadsheet to app's defined meal types
const MEAL_ALIASES = {
  // Dinner
  "supper":              "Dinner",
  "dinner":              "Dinner",
  "tea":                 "Dinner",
  // Lunch
  "lunch":               "Lunch",
  // Breakfast
  "breakfast":           "Breakfast",
  "brunch":              "Breakfast",
  "morning":             "Breakfast",
  // Snacks
  "snack":               "Snacks",
  "snacks":              "Snacks",
  "treat":               "Snacks",
  "treats":              "Snacks",
  "pre-workout":         "Snacks",
  // Protein Shake / Smoothie
  "protein shake":       "Protein Shake",
  "shake":               "Protein Shake",
  "post-workout":        "Protein Shake",
  "smoothie":            "Protein Shake",
  "green smoothie":      "Protein Shake",
  "fruit smoothie":      "Protein Shake",
  "mango smoothie":      "Protein Shake",
  "banana smoothie":     "Protein Shake",
  "berry smoothie":      "Protein Shake",
  "protein smoothie":    "Protein Shake",
  "recovery shake":      "Protein Shake",
  "meal replacement":    "Protein Shake",
};
function normaliseMeal(raw) {
  const key = String(raw || "").trim().toLowerCase();
  return MEAL_ALIASES[key] || "Dinner"; // default to Dinner if unrecognised
}

function cloneQuickMeals() {
  return Object.fromEntries(
    Object.entries(QUICK_MEAL_DEFAULTS).map(([meal, ingredients]) => [
      meal,
      ingredients.map(ing => ({ ...ing })),
    ])
  );
}

function quickIngredientForLog(ing) {
  const amount = parseFloat(ing.amount);
  const safeAmount = Number.isFinite(amount) ? amount : 0;
  if (ing.gramsPerUnit) {
    return {
      name: ing.name,
      amount: Math.round(safeAmount * ing.gramsPerUnit * 10) / 10,
      unit: "g",
      _source: "Quick Meals",
      _quickAmount: safeAmount,
      _quickUnit: ing.unit,
    };
  }
  return { name: ing.name, amount: safeAmount, unit: ing.unit || "g", _source: "Quick Meals" };
}

function enabledQuickIngredients(ingredients = []) {
  return ingredients
    .filter(ing => !ing.optional || ing.enabled)
    .map(quickIngredientForLog)
    .filter(ing => ing.name && Number(ing.amount) > 0);
}

/** Excel serial date → YYYY-MM-DD string */
function excelDateToISO(v) {
  if (!v) return null;
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0,10);
  if (typeof v === "number") {
    // Excel epoch: 1 = 1900-01-01, but Excel has a leap year bug for 1900
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    return d.toISOString().slice(0,10);
  }
  // Try parsing as date string
  const d = new Date(v);
  if (!isNaN(d)) return d.toISOString().slice(0,10);
  return null;
}

function parseMealLogFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      try {
        const data  = new Uint8Array(e.target.result);
        const wb    = XLSX.read(data, { type: "array", cellDates: false });
        const ws    = wb.Sheets[wb.SheetNames[0]];
        const rows  = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        if (rows.length < 2) return reject(new Error("Spreadsheet is empty or has only headers"));
        const headerIdx = findHeaderRow(rows);
        const headers = rows[headerIdx].map(h => String(h));
        const colMap  = mapMealLogHeaders(headers);
        if (colMap.ingredient < 0) return reject(new Error("Could not find an ingredient/food column."));
        if (colMap.calories   < 0) return reject(new Error("Could not find a calories/kcal column."));

        // Group rows into { date → { meal → [ingredients] } }
        // lastDate / lastMeal carry forward when a row leaves those columns blank
        // (common spreadsheet pattern: date/meal only on first row of each group)
        const dayMap = {};
        let lastDate = "unknown";
        let lastMeal = "Dinner";
        for (let i = headerIdx + 1; i < rows.length; i++) {
          const row = rows[i];
          const ingredient = String(row[colMap.ingredient] || "").trim();
          // Skip blank rows and TOTALS rows
          if (!ingredient || /^total/i.test(ingredient)) continue;
          const rawDate = colMap.date >= 0 ? row[colMap.date] : null;
          const parsedDate = excelDateToISO(rawDate);
          if (parsedDate) lastDate = parsedDate;
          const date = lastDate;
          const rawMeal = colMap.meal >= 0 ? String(row[colMap.meal] || "").trim() : "";
          if (rawMeal) lastMeal = normaliseMeal(rawMeal);
          const meal = lastMeal;
          const parsedAmount = colMap.amount >= 0 ? parseFloat(row[colMap.amount]) : NaN;
          const amount = Number.isFinite(parsedAmount) ? parsedAmount : 100;
          const unit    = colMap.unit >= 0 ? String(row[colMap.unit] || "").trim() : "g";
          const kcal    = parseFloat(row[colMap.calories]) || 0;
          const protein = colMap.protein >= 0 ? (parseFloat(row[colMap.protein]) || 0) : 0;
          const carbs   = colMap.carbs   >= 0 ? (parseFloat(row[colMap.carbs])   || 0) : 0;
          const fat     = colMap.fat     >= 0 ? (parseFloat(row[colMap.fat])     || 0) : 0;
          const fiber   = colMap.fiber   >= 0 ? (parseFloat(row[colMap.fiber])   || 0) : 0;
          const notes   = colMap.notes   >= 0 ? String(row[colMap.notes] || "").trim() : "";
          // Convert to per-100g for _nutrients (app stores per-100g, scales by amount)
          const per100 = amount > 0 ? {
            calories: Math.round((kcal    / amount) * 100 * 10) / 10,
            protein:  Math.round((protein / amount) * 100 * 10) / 10,
            carbs:    Math.round((carbs   / amount) * 100 * 10) / 10,
            fat:      Math.round((fat     / amount) * 100 * 10) / 10,
            fiber:    Math.round((fiber   / amount) * 100 * 10) / 10,
          } : { calories: kcal, protein, carbs, fat, fiber };
          if (!dayMap[date])       dayMap[date] = {};
          if (!dayMap[date][meal]) dayMap[date][meal] = [];
          dayMap[date][meal].push({ name: ingredient, amount, unit, _nutrients: per100, _source: "Imported", _per: "per 100g", notes });
        }
        const dates = Object.keys(dayMap);
        if (dates.length === 0) return reject(new Error("No valid rows found. Check that the spreadsheet has ingredient names and calorie values."));
        resolve({ dayMap, headers, colMap, rowCount: rows.length - 1, dates });
      } catch (err) {
        reject(err);
      }
    };
    reader.readAsArrayBuffer(file);
  });
}

export default function Foods({ customFoods, saveCustomFoods, logs, saveLogs, showToast }) {
  const fileRef      = useRef();
  const mlFileRef    = useRef();
  const [preview,    setPreview]    = useState(null); // { foods, headers, colMap }
  const [mlPreview,  setMlPreview]  = useState(null); // meal log preview
  const [error,      setError]      = useState("");
  const [mlError,    setMlError]    = useState("");
  const [loading,      setLoading]      = useState(false);
  const [mlLoading,    setMlLoading]    = useState(false);
  const [mealOverrides, setMealOverrides] = useState({}); // { "date|originalMeal": newMeal }
  const [mealImportDate, setMealImportDate] = useState("");
  const [search,     setSearch]     = useState("");
  const [tab,        setTab]        = useState("library"); // "library" | "import" | "mealimport"
  const [quickDate,  setQuickDate]  = useState(getTodayDate());
  const [quickMeals, setQuickMeals] = useState(() => cloneQuickMeals());

  function updateQuickIngredient(meal, idx, patch) {
    setQuickMeals(prev => ({
      ...prev,
      [meal]: (prev[meal] || []).map((ing, i) => i === idx ? { ...ing, ...patch } : ing),
    }));
  }

  function resetQuickMeal(meal) {
    setQuickMeals(prev => ({
      ...prev,
      [meal]: QUICK_MEAL_DEFAULTS[meal].map(ing => ({ ...ing })),
    }));
  }

  function addQuickIngredient(meal) {
    const ingredient = meal === "Protein Shake"
      ? { name: "", amount: "", unit: "g", smoothie: true }
      : { name: "", amount: 0, unit: "g" };
    setQuickMeals(prev => ({
      ...prev,
      [meal]: [...(prev[meal] || []), ingredient],
    }));
  }

  function removeQuickIngredient(meal, idx) {
    setQuickMeals(prev => ({
      ...prev,
      [meal]: (prev[meal] || []).filter((_, i) => i !== idx),
    }));
  }

  function saveQuickMeal(meal) {
    const ingredients = enabledQuickIngredients(quickMeals[meal]);
    if (!ingredients.length) {
      showToast("Nothing to log for that meal");
      return;
    }
    const macros = calcMacros(ingredients);
    const existing = (logs || []).find(l => l.date === quickDate);
    const updatedMeal = {
      ingredients,
      notes: "Logged from Quick Meals",
      macros,
      logged: true,
    };
    const updatedLogs = existing
      ? (logs || []).map(l => l.date === quickDate ? { ...l, meals: { ...(l.meals || {}), [meal]: updatedMeal } } : l)
      : [...(logs || []), { id: Date.now(), date: quickDate, meals: { [meal]: updatedMeal } }];
    saveLogs(updatedLogs);
    showToast(`✓ ${meal} logged for ${quickDate}: ${macros.calories} kcal, ${macros.protein}g protein`);
  }

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError("");
    setPreview(null);
    try {
      validateImportFile(file);
      // Auto-detect: if the spreadsheet has both a 'date' and 'meal' column, treat it as a meal log
      const sniffResult = await sniffFormat(file);
      if (sniffResult === "meallog") {
        // Route to meal log import automatically
        const result = await parseMealLogFile(file);
        setMlPreview(result);
        setMealImportDate(result.colMap.date >= 0 ? "" : getTodayDate());
        setMealOverrides({});
        setTab("mealimport");
        showToast("📋 Meal log format detected — switched to Supper Import");
      } else {
        const result = await parseFile(file);
        setPreview(result);
        setMealImportDate("");
        setTab("import");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  }

  function confirmImport(mode) {
    if (!preview) return;
    const incoming = preview.foods;
    let updated;
    if (mode === "replace") {
      updated = incoming;
    } else {
      // Merge: incoming overwrites existing entries with same name (case-insensitive)
      const existingMap = Object.fromEntries((customFoods || []).map(f => [f.name.toLowerCase(), f]));
      incoming.forEach(f => { existingMap[f.name.toLowerCase()] = f; });
      updated = Object.values(existingMap);
    }
    saveCustomFoods(updated);
    showToast(`✓ ${incoming.length} foods imported (${updated.length} total in library)`);
    setPreview(null);
    setTab("library");
  }

  function deleteFood(name) {
    const updated = (customFoods || []).filter(f => f.name !== name);
    saveCustomFoods(updated);
    showToast("Food removed");
  }

  function clearAll() {
    saveCustomFoods([]);
    showToast("Food library cleared");
  }

  async function handleMealLogFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setMlLoading(true);
    setMlError("");
    setMlPreview(null);
    try {
      validateImportFile(file);
      const result = await parseMealLogFile(file);
      setMlPreview(result);
      setMealOverrides({});
      setMealImportDate(result.colMap.date >= 0 ? "" : getTodayDate());
    } catch (err) {
      setMlError(err.message);
    } finally {
      setMlLoading(false);
      e.target.value = "";
    }
  }

  function confirmMealLogImport() {
    if (!mlPreview) return;
    const { dayMap } = mlPreview;
    const overrideDate = mealImportDate.trim();
    if (!overrideDate && Object.keys(dayMap).some(date => date === "unknown")) {
      setMlError("Choose an import date before importing this spreadsheet.");
      return;
    }

    // Apply any meal overrides the user set in the preview
    const remapped = {};
    for (const [date, meals] of Object.entries(dayMap)) {
      const targetDate = overrideDate || date;
      if (!remapped[targetDate]) remapped[targetDate] = {};
      for (const [mealName, ingredients] of Object.entries(meals)) {
        const target = mealOverrides[`${date}|${mealName}`] || mealName;
        if (!remapped[targetDate][target]) remapped[targetDate][target] = [];
        remapped[targetDate][target].push(...ingredients);
      }
    }

    const updatedLogs = [...(logs || [])];
    let added = 0, merged = 0, skipped = 0;
    for (const [date, meals] of Object.entries(remapped)) {
      const existingIdx = updatedLogs.findIndex(l => l.date === date);
      if (existingIdx >= 0) {
        const existingLog = { ...updatedLogs[existingIdx] };
        const existingMeals = { ...(existingLog.meals || {}) };
        for (const [mealName, ingredients] of Object.entries(meals)) {
          const newIngredients = ingredients.map(({notes: _n, ...i}) => i);
          const newNotes = ingredients.map(i => i.notes).filter(Boolean).join("; ");
          if (existingMeals[mealName]) {
            if (ingredientBatchExists(existingMeals[mealName].ingredients || [], newIngredients)) {
              skipped++;
              continue;
            }
            const mergedIngredients = [...(existingMeals[mealName].ingredients || []), ...newIngredients];
            existingMeals[mealName] = { ...existingMeals[mealName], ingredients: mergedIngredients, notes: [existingMeals[mealName].notes, newNotes].filter(Boolean).join("; "), macros: calcMacros(mergedIngredients), logged: true };
          } else {
            existingMeals[mealName] = { ingredients: newIngredients, notes: newNotes, macros: calcMacros(ingredients), logged: true };
          }
        }
        existingLog.meals = existingMeals;
        updatedLogs[existingIdx] = existingLog;
        merged++;
      } else {
        const mealEntries = {};
        for (const [mealName, ingredients] of Object.entries(meals)) {
          mealEntries[mealName] = { ingredients: ingredients.map(({notes: _n, ...i}) => i), notes: ingredients.map(i => i.notes).filter(Boolean).join("; "), macros: calcMacros(ingredients), logged: true };
        }
        updatedLogs.push({ id: Date.now() + Math.random(), date, meals: mealEntries });
        added++;
      }
    }
    saveLogs(updatedLogs);
    showToast(`✓ Imported ${added} new day${added !== 1 ? "s" : ""}, merged ${merged} existing${skipped ? `, skipped ${skipped} duplicate meal${skipped !== 1 ? "s" : ""}` : ""}`);
    setMlPreview(null);
    setMealOverrides({});
    setMealImportDate("");
    setTab("mealimport");
  }

  const filtered = (customFoods || []).filter(f =>
    !search || f.name.toLowerCase().includes(search.toLowerCase())
  );

  function renderQuickMealCard(meal) {
    const ingredients = quickMeals[meal] || [];
    const resolved = enabledQuickIngredients(ingredients);
    const macros = calcMacros(resolved);
    return (
      <div key={meal} style={{ ...CARD, padding: "16px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#e2e8f0" }}>{meal}</div>
            <div style={{ fontSize: 12, color: "#777", marginTop: 3 }}>
              {macros.calories} kcal · {macros.protein}g protein · {macros.carbs}g carbs · {macros.fat}g fat
            </div>
          </div>
          <button onClick={() => resetQuickMeal(meal)} style={{ ...btn, padding: "6px 9px", fontSize: 11, background: "rgba(255,255,255,0.05)", color: "#888" }}>
            Reset
          </button>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {ingredients.map((ing, idx) => {
            const disabled = ing.optional && !ing.enabled;
            const canRemove = meal === "Protein Shake" && ingredients.length > 1;
            const columns = `${ing.optional ? "28px " : ""}1fr 86px 82px ${canRemove ? "34px" : ""}`.trim();
            const nameControl = ing.kind === "bread" ? (
              <select
                value={ing.name}
                onChange={e => updateQuickIngredient(meal, idx, { name: e.target.value })}
                style={{ ...inputStyle, padding: "8px 10px" }}
              >
                {QUICK_BREADS.map(bread => <option key={bread} value={bread}>{bread}</option>)}
              </select>
            ) : ing.smoothie ? (
              <select
                value={ing.name}
                onChange={e => updateQuickIngredient(meal, idx, { name: e.target.value })}
                style={{ ...inputStyle, padding: "8px 10px" }}
              >
                <option value="">Choose ingredient</option>
                {QUICK_SMOOTHIE_INGREDIENTS.map(item => <option key={item} value={item}>{item}</option>)}
              </select>
            ) : (
              <div style={{ fontSize: 13, color: disabled ? "#555" : "#cbd5e1" }}>{ing.name}</div>
            );

            return (
              <div key={`${meal}-${idx}`} style={{ display: "grid", gridTemplateColumns: columns, gap: 8, alignItems: "center" }}>
                {ing.optional && (
                  <input
                    type="checkbox"
                    checked={!!ing.enabled}
                    onChange={e => updateQuickIngredient(meal, idx, { enabled: e.target.checked })}
                    aria-label={`Include ${ing.name}`}
                  />
                )}
                {nameControl}
                <input
                  type="number"
                  min="0"
                  step={ing.unit === "cups" ? "0.01" : "1"}
                  value={ing.amount}
                  disabled={disabled}
                  onChange={e => updateQuickIngredient(meal, idx, { amount: e.target.value })}
                  style={{ ...inputStyle, padding: "8px 10px", opacity: disabled ? 0.45 : 1 }}
                />
                <select
                  value={ing.unit}
                  disabled={disabled || !!ing.gramsPerUnit}
                  onChange={e => updateQuickIngredient(meal, idx, { unit: e.target.value })}
                  style={{ ...inputStyle, color: "#888", padding: "8px 10px", opacity: disabled ? 0.45 : 1 }}
                >
                  <option value="g">g</option>
                  <option value="ml">ml</option>
                  <option value="cups">cups</option>
                  {ing.gramsPerUnit && <option value={ing.unit}>{ing.unit} ({ing.gramsPerUnit}g)</option>}
                </select>
                {canRemove && (
                  <button onClick={() => removeQuickIngredient(meal, idx)} style={{ ...btn, padding: "7px 9px", fontSize: 12, color: "#f87171", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.15)" }}>x</button>
                )}
              </div>
            );
          })}
        </div>

        {meal === "Protein Shake" && (
          <button onClick={() => addQuickIngredient(meal)}
            style={{ ...btn, padding: "8px 12px", fontSize: 12, background: "rgba(96,165,250,0.1)", color: "#60a5fa", border: "1px solid rgba(96,165,250,0.22)" }}>
            + Add smoothie ingredient
          </button>
        )}

        <button onClick={() => saveQuickMeal(meal)}
          style={{ ...btn, padding: "10px 14px", fontSize: 13, background: "rgba(52,211,153,0.14)", color: "#34d399", border: "1px solid rgba(52,211,153,0.28)" }}>
          Log {meal}
        </button>
      </div>
    );
  }

  const inputStyle = {
    background: "rgba(0,0,0,0.4)", border: "1px solid rgba(167,139,250,0.2)",
    borderRadius: 8, padding: "8px 12px", color: "#fff", fontSize: 13,
    outline: "none", width: "100%", boxSizing: "border-box",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: 26, fontWeight: 600, margin: 0, letterSpacing: -0.5 }}>Food Library</h1>
        <p style={{ color: "#999", margin: "4px 0 0", fontSize: 14 }}>
          Quick staple meals, your food database, and supper spreadsheet imports
        </p>
      </div>

      {/* Sub-tabs */}
      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid rgba(255,255,255,0.06)", paddingBottom: 14 }}>
        {[["library", "📚", "Library", "#a78bfa"], ["import", "⚡", "Quick Meals", "#34d399"], ["mealimport", "🍽", "Supper Import", "#fb923c"]].map(([t, icon, label, c]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            ...btn, padding: "8px 16px", fontSize: 12,
            background: tab === t ? `${c}22` : "rgba(255,255,255,0.03)",
            color:      tab === t ? c : "#444",
            border:     `1px solid ${tab === t ? c + "55" : "rgba(255,255,255,0.07)"}`,
            fontWeight: tab === t ? 600 : 400,
          }}>{icon} {label}</button>
        ))}
      </div>

      {/* ── LIBRARY TAB ── */}
      {tab === "library" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {/* Stats + actions */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <div style={{ ...CARD, padding: "10px 18px", flex: 1, minWidth: 160 }}>
              <span style={{ ...MONO, fontSize: 10, color: "#b8a8d0", textTransform: "uppercase", letterSpacing: 1 }}>Foods in library</span>
              <div style={{ ...MONO, fontSize: 22, fontWeight: 700, color: "#a78bfa", marginTop: 4 }}>{(customFoods || []).length}</div>
            </div>
            <button onClick={() => { setTab("import"); setPreview(null); setError(""); }} style={{ ...btn, padding: "10px 18px", fontSize: 13, background: "rgba(52,211,153,0.12)", color: "#34d399", border: "1px solid rgba(52,211,153,0.25)" }}>
              📥 Import Spreadsheet
            </button>
            {(customFoods || []).length > 0 && (
              <button onClick={clearAll} style={{ ...btn, padding: "10px 14px", fontSize: 13, background: "rgba(248,113,113,0.08)", color: "#f87171", border: "1px solid rgba(248,113,113,0.2)" }}>
                🗑 Clear All
              </button>
            )}
          </div>

          {/* Search */}
          {(customFoods || []).length > 0 && (
            <input
              type="text"
              placeholder="Search your foods…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={inputStyle}
            />
          )}

          {/* Food list */}
          {(customFoods || []).length === 0 ? (
            <div style={{ ...CARD, padding: 40, textAlign: "center", color: "#b8a8d0", fontSize: 13 }}>
              No foods imported yet. Upload a CSV or Excel spreadsheet to build your library.
              <br/><br/>
              <span style={{ fontSize: 12, color: "#666" }}>
                Expected columns: <span style={{ color: "#a78bfa" }}>Name, Calories, Protein, Carbs, Fat, Fiber</span> (per 100g)
              </span>
            </div>
          ) : (
            <div style={{ ...CARD, overflow: "hidden" }}>
              {/* Column headers */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 70px 70px 70px 36px", gap: 8, padding: "10px 16px", borderBottom: "1px solid rgba(167,139,250,0.1)", ...MONO, fontSize: 9, color: "#b8a8d0", textTransform: "uppercase", letterSpacing: 1 }}>
                <span>Name</span><span>Cal</span><span>Prot</span><span>Carbs</span><span>Fat</span><span>Fiber</span><span></span>
              </div>
              <div style={{ maxHeight: 480, overflowY: "auto" }}>
                {filtered.length === 0 ? (
                  <div style={{ padding: "20px 16px", color: "#666", fontSize: 13 }}>No matches for "{search}"</div>
                ) : filtered.map((f, i) => (
                  <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 70px 70px 70px 36px", gap: 8, padding: "9px 16px", borderBottom: "1px solid rgba(167,139,250,0.06)", alignItems: "center" }}>
                    <span style={{ fontSize: 13, color: "#e2e8f0" }}>{f.name}</span>
                    <span style={{ ...MONO, fontSize: 12, color: "#fb923c" }}>{f.calories}</span>
                    <span style={{ ...MONO, fontSize: 12, color: "#60a5fa" }}>{f.protein}g</span>
                    <span style={{ ...MONO, fontSize: 12, color: "#4ade80" }}>{f.carbs}g</span>
                    <span style={{ ...MONO, fontSize: 12, color: "#fbbf24" }}>{f.fat}g</span>
                    <span style={{ ...MONO, fontSize: 12, color: "#c084fc" }}>{f.fiber}g</span>
                    <button onClick={() => deleteFood(f.name)} style={{ ...btn, padding: "3px 6px", fontSize: 11, color: "#f87171", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.15)" }}>✕</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── IMPORT TAB ── */}
      {tab === "import" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ ...CARD, padding: "16px 20px", border: "1px solid rgba(52,211,153,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "end", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
              <div>
                <div style={{ ...MONO, fontSize: 11, color: "#34d399", textTransform: "uppercase", letterSpacing: 1 }}>Quick Meals</div>
                <div style={{ fontSize: 13, color: "#c4b4d8", marginTop: 5 }}>
                  Log your usual breakfast, lunch, or smoothie by changing only the amounts.
                </div>
              </div>
              <label style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 160 }}>
                <span style={{ ...MONO, fontSize: 10, color: "#34d399", textTransform: "uppercase", letterSpacing: 1 }}>Log date</span>
                <input type="date" value={quickDate} onChange={e => setQuickDate(e.target.value)} style={{ ...inputStyle, borderColor: "rgba(52,211,153,0.35)" }} />
              </label>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 12 }}>
              {["Breakfast", "Lunch", "Protein Shake"].map(renderQuickMealCard)}
            </div>
            <div style={{ fontSize: 12, color: "#777", marginTop: 12, lineHeight: 1.5 }}>
              Lunch bread is logged in grams, eggs are converted at 1 egg = 50g, and smoothie rows can be added or removed. A second tap updates that meal for the date instead of adding duplicates.
            </div>
          </div>

          {/* Instructions */}
          <div style={{ ...CARD, padding: "16px 20px" }}>
            <div style={{ ...MONO, fontSize: 11, color: "#a78bfa", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Food library spreadsheet import</div>
            <div style={{ fontSize: 13, color: "#c4b4d8", lineHeight: 1.7 }}>
              Use this only when adding foods to your reusable library. Create a <strong style={{ color: "#e2e8f0" }}>CSV or Excel (.xlsx/.xls)</strong> file with these columns (in any order, column names are flexible):
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(130px,1fr))", gap: 8, marginTop: 12 }}>
              {[["Name *", "Food / Item / Ingredient", "#a78bfa"], ["Calories *", "Cal / kcal / Energy", "#fb923c"], ["Protein", "Pro / Prot", "#60a5fa"], ["Carbs", "Carb / CHO", "#4ade80"], ["Fat", "Fat / Lipid", "#fbbf24"], ["Fiber", "Fiber / Fibre", "#c084fc"]].map(([col, aliases, c]) => (
                <div key={col} style={{ background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ ...MONO, fontSize: 10, color: c, marginBottom: 3 }}>{col}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{aliases}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>* Required. All values are <strong style={{ color: "#999" }}>per 100g</strong>. Rows without a name or calories are skipped.</div>
          </div>

          {/* Upload button */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => fileRef.current?.click()} disabled={loading}
              style={{ ...btn, padding: "11px 20px", fontSize: 13, background: "rgba(52,211,153,0.12)", color: loading ? "#888" : "#34d399", border: "1px solid rgba(52,211,153,0.25)" }}>
              {loading ? "Reading file…" : "📂 Choose Food Library File (.csv / .xlsx / .xls)"}
            </button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={handleFile} />
            <span style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>Parsed entirely in-browser — no API used</span>
          </div>

          {/* Error */}
          {error && (
            <div style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 12, padding: "12px 16px", color: "#f87171", fontSize: 13 }}>
              ⚠️ {error}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Column mapping summary */}
              <div style={{ ...CARD, padding: "14px 18px" }}>
                <div style={{ ...MONO, fontSize: 11, color: "#34d399", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Detected {preview.foods.length} foods — column mapping
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {Object.entries(preview.colMap).map(([field, idx]) => (
                    <span key={field} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, background: idx >= 0 ? "rgba(52,211,153,0.12)" : "rgba(255,255,255,0.04)", color: idx >= 0 ? "#34d399" : "#555", border: `1px solid ${idx >= 0 ? "rgba(52,211,153,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                      {field}: {idx >= 0 ? `"${preview.headers[idx]}"` : "not found"}
                    </span>
                  ))}
                </div>
              </div>

              {/* Preview rows */}
              <div style={{ ...CARD, overflow: "hidden" }}>
                <div style={{ padding: "10px 16px", borderBottom: "1px solid rgba(167,139,250,0.1)", ...MONO, fontSize: 10, color: "#b8a8d0", textTransform: "uppercase", letterSpacing: 1 }}>
                  Preview (first 10 rows)
                </div>
                <div style={{ overflowX: "auto" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 70px 70px 70px", gap: 8, padding: "8px 16px", borderBottom: "1px solid rgba(167,139,250,0.08)", ...MONO, fontSize: 9, color: "#888", textTransform: "uppercase" }}>
                    <span>Name</span><span>Cal</span><span>Prot</span><span>Carbs</span><span>Fat</span><span>Fiber</span>
                  </div>
                  {preview.foods.slice(0, 10).map((f, i) => (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 70px 70px 70px 70px 70px", gap: 8, padding: "8px 16px", borderBottom: "1px solid rgba(167,139,250,0.05)", alignItems: "center" }}>
                      <span style={{ fontSize: 12, color: "#e2e8f0" }}>{f.name}</span>
                      <span style={{ ...MONO, fontSize: 11, color: "#fb923c" }}>{f.calories}</span>
                      <span style={{ ...MONO, fontSize: 11, color: "#60a5fa" }}>{f.protein}g</span>
                      <span style={{ ...MONO, fontSize: 11, color: "#4ade80" }}>{f.carbs}g</span>
                      <span style={{ ...MONO, fontSize: 11, color: "#fbbf24" }}>{f.fat}g</span>
                      <span style={{ ...MONO, fontSize: 11, color: "#c084fc" }}>{f.fiber}g</span>
                    </div>
                  ))}
                  {preview.foods.length > 10 && (
                    <div style={{ padding: "8px 16px", color: "#666", fontSize: 12 }}>…and {preview.foods.length - 10} more</div>
                  )}
                </div>
              </div>

              {/* Import actions */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button onClick={() => confirmImport("merge")}
                  style={{ ...btn, padding: "10px 20px", fontSize: 13, background: "rgba(52,211,153,0.2)", color: "#34d399", border: "1px solid rgba(52,211,153,0.4)" }}>
                  ✓ Merge with existing ({(customFoods || []).length} existing)
                </button>
                <button onClick={() => confirmImport("replace")}
                  style={{ ...btn, padding: "10px 20px", fontSize: 13, background: "rgba(167,139,250,0.12)", color: "#a78bfa", border: "1px solid rgba(167,139,250,0.3)" }}>
                  ↺ Replace library entirely
                </button>
                <button onClick={() => { setPreview(null); setError(""); }}
                  style={{ ...btn, padding: "10px 14px", fontSize: 13, background: "rgba(255,255,255,0.05)", color: "#777" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── SUPPER IMPORT TAB ── */}
      {tab === "mealimport" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Format guide */}
          <div style={{ ...CARD, padding: "16px 20px" }}>
            <div style={{ ...MONO, fontSize: 11, color: "#fb923c", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>Supper spreadsheet import</div>
            <div style={{ fontSize: 13, color: "#c4b4d8", lineHeight: 1.7 }}>
              Use this for the changing meal. Your spreadsheet should have one row per ingredient. Required columns:
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(150px,1fr))", gap: 8, marginTop: 12 }}>
              {[
                ["Date *",       "YYYY-MM-DD or Excel date",  "#fb923c"],
                ["Meal *",       "Breakfast / Supper / etc.", "#fb923c"],
                ["Ingredient *", "Food name",                 "#fb923c"],
                ["Amount",       "Grams or serving size",     "#fbbf24"],
                ["Unit",         "g raw / g dry / etc.",      "#fbbf24"],
                ["kcal *",       "Calories for that amount",  "#fb923c"],
                ["Protein (g)",  "For the amount listed",     "#60a5fa"],
                ["Carbs (g)",    "For the amount listed",     "#4ade80"],
                ["Fat (g)",      "For the amount listed",     "#fbbf24"],
                ["Fibre (g)",    "For the amount listed",     "#c084fc"],
                ["Notes",        "Optional notes",            "#888"],
              ].map(([col, desc, c]) => (
                <div key={col} style={{ background: "rgba(0,0,0,0.25)", borderRadius: 8, padding: "8px 12px" }}>
                  <div style={{ ...MONO, fontSize: 10, color: c, marginBottom: 3 }}>{col}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{desc}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 12, color: "#666", marginTop: 10 }}>
              * Required. <strong style={{ color: "#999" }}>TOTALS rows are automatically skipped.</strong> Macros are stored per-amount (not per 100g) so values are exact.
            </div>
          </div>

          {/* Upload button */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button onClick={() => mlFileRef.current?.click()} disabled={mlLoading}
              style={{ ...btn, padding: "11px 20px", fontSize: 13, background: "rgba(251,146,60,0.12)", color: mlLoading ? "#888" : "#fb923c", border: "1px solid rgba(251,146,60,0.25)" }}>
              {mlLoading ? "Reading file…" : "📂 Choose Supper / Meal Log File (.csv / .xlsx / .xls)"}
            </button>
            <input ref={mlFileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={handleMealLogFile} />
            <span style={{ fontSize: 12, color: "#555", fontStyle: "italic" }}>Parsed entirely in-browser — no API used</span>
          </div>

          {/* Error */}
          {mlError && (
            <div style={{ background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.25)", borderRadius: 12, padding: "12px 16px", color: "#f87171", fontSize: 13 }}>
              ⚠️ {mlError}
            </div>
          )}

          {/* Preview */}
          {mlPreview && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Summary */}
              <div style={{ ...CARD, padding: "14px 18px" }}>
                <div style={{ ...MONO, fontSize: 11, color: "#fb923c", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
                  Found {mlPreview.dates.length} day{mlPreview.dates.length !== 1 ? "s" : ""} — column mapping
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 12, alignItems: "end", marginBottom: 12 }}>
                  <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <span style={{ ...MONO, fontSize: 10, color: "#fb923c", textTransform: "uppercase", letterSpacing: 1 }}>Import date override</span>
                    <input
                      type="date"
                      value={mealImportDate}
                      onChange={e => setMealImportDate(e.target.value)}
                      style={{ ...inputStyle, borderColor: "rgba(251,146,60,0.35)" }}
                    />
                  </label>
                  <div style={{ fontSize: 12, color: "#888", lineHeight: 1.45 }}>
                    Leave blank to use the spreadsheet dates. Pick a date to import an old spreadsheet as one new day.
                  </div>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {Object.entries(mlPreview.colMap).map(([field, idx]) => (
                    <span key={field} style={{ fontSize: 12, padding: "3px 10px", borderRadius: 6, background: idx >= 0 ? "rgba(251,146,60,0.1)" : "rgba(255,255,255,0.04)", color: idx >= 0 ? "#fb923c" : "#555", border: `1px solid ${idx >= 0 ? "rgba(251,146,60,0.3)" : "rgba(255,255,255,0.08)"}` }}>
                      {field}: {idx >= 0 ? `"${mlPreview.headers[idx]}"` : "not found"}
                    </span>
                  ))}
                </div>
              </div>

              {/* Meal assignment — always shown */}
              <div style={{ ...CARD, padding: "14px 18px", border: "1px solid rgba(251,146,60,0.3)" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#fb923c", marginBottom: 4 }}>
                  Where should each entry be logged?
                </div>
                <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
                  {mlPreview.colMap.meal >= 0
                    ? "Meal names were detected and mapped automatically. Change any that look wrong."
                    : "⚠ No meal column found — all items defaulted to Dinner. Reassign below."}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {mlPreview.dates.map(date => (
                    <div key={date} style={{ background: "rgba(0,0,0,0.2)", borderRadius: 8, padding: "10px 14px" }}>
                      <div style={{ ...MONO, fontSize: 11, color: "#fb923c", marginBottom: 8 }}>{date}</div>
                      {Object.entries(mlPreview.dayMap[date]).map(([meal, ingredients]) => {
                        const totalKcal = ingredients.reduce((s, i) => {
                          const amount = parseFloat(i.amount);
                          const g = Number.isFinite(amount) ? amount : 100;
                          return s + Math.round((i._nutrients.calories * g) / 100);
                        }, 0);
                        const overrideKey = `${date}|${meal}`;
                        const currentMeal = mealOverrides[overrideKey] || meal;
                        return (
                          <div key={meal} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6, flexWrap: "wrap" }}>
                            <label style={{ fontSize: 12, color: "#aaa", minWidth: 60 }}>Log as:</label>
                            <select
                              value={currentMeal}
                              onChange={e => setMealOverrides(prev => ({ ...prev, [overrideKey]: e.target.value }))}
                              style={{ background: "rgba(251,146,60,0.15)", color: "#fb923c", border: "1px solid rgba(251,146,60,0.4)", borderRadius: 6, padding: "5px 10px", fontSize: 13, cursor: "pointer", fontWeight: 600 }}
                            >
                              {["Breakfast","Lunch","Dinner","Snacks","Protein Shake"].map(m => (
                                <option key={m} value={m} style={{ background: "#1a0f2e", color: "#e2e8f0" }}>{m}</option>
                              ))}
                            </select>
                            <span style={{ fontSize: 12, color: "#888" }}>{ingredients.length} item{ingredients.length !== 1 ? "s" : ""}</span>
                            <span style={{ ...MONO, fontSize: 12, color: "#fb923c", marginLeft: "auto" }}>{totalKcal} kcal</span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={() => confirmMealLogImport()}
                  style={{ ...btn, padding: "10px 20px", fontSize: 13, background: "rgba(251,146,60,0.2)", color: "#fb923c", border: "1px solid rgba(251,146,60,0.4)" }}>
                  ✓ Import & merge into logs
                </button>
                <button onClick={() => { setMlPreview(null); setMlError(""); setMealOverrides({}); setMealImportDate(""); }}
                  style={{ ...btn, padding: "10px 14px", fontSize: 13, background: "rgba(255,255,255,0.05)", color: "#777" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
